import { ChangeEvent, useEffect, useState } from 'react';
import { TextInput, TextInputProps } from '../TextInput';
import { transformForRendering, stripNonFloatChars, transformForValidation } from './utils';

export type CurrencyInputProps = Omit<TextInputProps, 'placeholder' | 'value' | 'onChange'> & {
  id: string;
  label: string;
  amount?: number;
  prefix?: string;
  onChange: (value: number | undefined) => void;
};

export const CurrencyInput = ({
  id,
  label,
  amount,
  onChange,
  prefix = '$',
  ...textInputProps
}: CurrencyInputProps) => {
  const transformForRenderingWithFallback = (amt?: number | string) =>
    (amt && transformForRendering(amt, prefix)) || '';

  const [renderedValue, setRenderedValue] = useState<string>(transformForRenderingWithFallback(amount));
  const [amountValue, setAmountValue] = useState<number | undefined>(amount);

  useEffect(() => {
    setRenderedValue(transformForRenderingWithFallback(amountValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);

  const handleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const strippedValue = stripNonFloatChars(value);

    if (!strippedValue) {
      setRenderedValue('');
      setAmountValue(undefined);
      onChange(undefined);
      return;
    }

    const newAmountValue = transformForValidation(strippedValue);

    setRenderedValue(transformForRenderingWithFallback(strippedValue));
    setAmountValue(newAmountValue);
    onChange(newAmountValue);
  };

  return (
    <>
      <TextInput
        id={id}
        label={label}
        placeholder={`${prefix}0.00`}
        value={renderedValue}
        onChange={handleChange}
        {...textInputProps}
      />
      <input id={`${id}-value`} type="hidden" value={amountValue || ''} hidden />
    </>
  );
};
